import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const BackIcon = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>);

export default function CategoryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [exerciseCounts, setExerciseCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    setLoading(true);
    const { data: cat } = await supabase.from('categories').select('*').eq('id', id).single();
    const { data: subs } = await supabase.from('subcategories').select('*').eq('category_id', id).order('sort_order');
    const { data: exs } = await supabase.from('exercises').select('subcategory_id').eq('category_id', id).eq('active', true);

    if (cat) setCategory(cat);
    if (subs) setSubcategories(subs);
    if (exs) {
      const counts = {};
      exs.forEach(e => {
        const key = e.subcategory_id || 'none';
        counts[key] = (counts[key] || 0) + 1;
      });
      setExerciseCounts(counts);
    }
    setLoading(false);
  };

  if (loading) return <LoadingSpinner />;
  if (!category) return null;

  const color = category.color || '#FF5500';
  const totalExercises = Object.values(exerciseCounts).reduce((a, b) => a + b, 0);

  return (
    <div style={{ fontFamily: "'Barlow', sans-serif", background: '#111009', minHeight: '100vh', color: '#F0EBE3', maxWidth: 'var(--app-width, 860px)', margin: '0 auto', paddingBottom: 40 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700&family=Barlow+Condensed:wght@400;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        button { cursor: pointer; border: none; font-family: inherit; transition: all 0.15s; }
        button:active { transform: scale(0.96); opacity: 0.82; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        .fu { animation: fadeUp 0.25s ease forwards; }
        .bg-grid { position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: linear-gradient(rgba(255,85,0,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,85,0,0.02) 1px, transparent 1px);
          background-size: 40px 40px; }
        .sub-card { background: #1C1916; border: 1px solid #2A2520; border-radius: 12px;
          padding: 16px 14px; cursor: pointer; transition: all 0.18s; position: relative; overflow: hidden; }
        .sub-card:hover { transform: translateY(-2px); }
        .sub-card:active { transform: scale(0.97); opacity: 0.85; }
      `}</style>

      <div className="bg-grid"/>

      {/* Header */}
      <div style={{
        padding: '52px 20px 20px',
        background: `linear-gradient(180deg, ${color}15 0%, #111009 100%)`,
        borderBottom: `1px solid ${color}18`,
        position: 'relative', zIndex: 1,
      }}>
        <button onClick={() => navigate('/exercises')} style={{ background: 'transparent', color: '#7A7268', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: "'Barlow Condensed'", fontWeight: 700, letterSpacing: 1 }}>
          <BackIcon/> EXERCISES
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {category.image_url
            ? <img src={category.image_url} alt="" style={{ width: 60, height: 60, borderRadius: 14, objectFit: 'cover', border: `2px solid ${color}44` }}/>
            : <div style={{ width: 60, height: 60, borderRadius: 14, background: color + '22', border: `2px solid ${color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>{category.emoji || '🥁'}</div>
          }
          <div>
            <h1 style={{ fontFamily: "'Barlow Condensed'", fontSize: 34, fontWeight: 900, letterSpacing: 1, color: '#F0EBE3', lineHeight: 1 }}>{category.name}</h1>
            <div style={{ fontSize: 12, color: '#7A7268', marginTop: 4 }}>
              {subcategories.length} subcategories · {totalExercises} exercises
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '20px 16px', position: 'relative', zIndex: 1 }}>
        <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 12, fontWeight: 700, letterSpacing: 2, color: '#7A7268', marginBottom: 14 }}>
          SUBCATEGORIES
        </div>

        {subcategories.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#3A3530' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🥁</div>
            <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 16, fontWeight: 700, letterSpacing: 1 }}>SEM SUBCATEGORIES</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Adiciona subcategories no painel de admin</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          {subcategories.map((sub, i) => {
            const count = exerciseCounts[sub.id] || 0;
            return (
              <div key={sub.id} className={`sub-card fu`} style={{ animationDelay: `${i * 0.04}s`, opacity: 0 }}
                onClick={() => navigate(`/subcategory/${sub.id}`)}>
                {/* Color accent */}
                <div style={{ position: 'absolute', bottom: -12, right: -12, width: 50, height: 50, borderRadius: '50%', background: color, opacity: 0.08 }}/>
                {/* Bottom bar */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: color, opacity: 0.4, borderRadius: '0 0 12px 12px' }}/>

                <div style={{ fontSize: 13, fontWeight: 700, color: '#F0EBE3', lineHeight: 1.3, marginBottom: 8 }}>{sub.name}</div>
                <div style={{ fontSize: 11, color: '#5A5248' }}>{count} ex.</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div style={{ minHeight: '100vh', background: '#111009', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #2A2520', borderTopColor: '#FF5500', animation: 'spin 0.8s linear infinite' }}/>
    </div>
  );
}
